"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import React, { useState } from "react"
import dynamic from "next/dynamic"
import autocompleteSearch, {
  type AutocompleteSearchQueryData,
  type AutocompleteSearchQueryVariables,
} from "../../../../graphql/queries/autocompleteSearch"
import { useQuery } from "@apollo/client/react"
import SearchBarUnfocused from "./SearchBarUnfocused"
import useGoogleAutocomplete from "../../../../hooks/useGoogleAutocomplete"
import {
  SEARCH_TYPE_GROUPS,
  SEARCH_TYPE_PROVIDERS,
} from "../../../../lib/search/searchParams"

// Loaded on demand (first focus) so the focused UI's heavy MUI deps aren't in the
// initial home bundle. ssr:false is safe: the focused UI never renders on the
// server (searchBarFocused starts false).
const SearchBarFocused = dynamic(() => import("./SearchBarFocused"), {
  ssr: false,
})

const SearchBar = ({
  searchType = SEARCH_TYPE_GROUPS,
  initialSearchTerm,
  handleUpdateSearch,
  initalLatitude,
  initalLongitude,
  initialLocation,
}) => {
  const isProviderSearch = searchType === SEARCH_TYPE_PROVIDERS
  const searchLabel = isProviderSearch ? "Name" : "Group"
  const searchPlaceholder = isProviderSearch
    ? "Search a provider name"
    : "What topic do you want to explore in your group work?"
  const searchHeading = isProviderSearch
    ? "Find a Provider"
    : "Find Mental Health Groups"
  const [searchBarFocused, setSearchBarFocused] = useState(false)
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || "")
  const [location, setLocation] = useState(initialLocation || "")
  const [placeId, setPlaceId] = useState<string | undefined>(undefined)
  const [latitude, setLatitude] = useState(initalLatitude)
  const [longitude, setLongitude] = useState(initalLongitude)

  // Re-sync local state when the source values change (adjust-state-in-render
  // pattern, see react.dev "You Might Not Need an Effect")
  const [prevInitialLocation, setPrevInitialLocation] = useState(initialLocation)
  if (initialLocation !== prevInitialLocation) {
    setPrevInitialLocation(initialLocation)
    setLocation(initialLocation || "")
  }

  const [prevInitialSearchTerm, setPrevInitialSearchTerm] =
    useState(initialSearchTerm)
  if (initialSearchTerm !== prevInitialSearchTerm) {
    setPrevInitialSearchTerm(initialSearchTerm)
    setSearchTerm(initialSearchTerm || "")
  }

  // this hook is primarily used for getting google autocomplete suggestions
  const [
    { value, inputValue, options },
    { setValue, setInputValue, setOptions, geoCodeOption, fetchPlacePredictions },
  ] = useGoogleAutocomplete({
    valueDefaultState: location,
    queryOptions: {
      componentRestrictions: { country: "us" },
      types: ["(cities)"],
      fields: ["address_components", "geometry"],
    },
  })

  // this function is for getting group search string autosuggest results
  const {
    loading: loadingAutocompleteSearch,
    error: errorAutocompleteSearch,
    data: dataAutocompleteSearch,
    refetch,
  } = useQuery<AutocompleteSearchQueryData, AutocompleteSearchQueryVariables>(
    autocompleteSearch,
    {
      skip: !searchTerm,
      variables: {
        searchValue: searchTerm,
      },
    },
  )

  const autocompleteGroupOptions = (
    dataAutocompleteSearch?.autocompleteSearch?.nodes ?? []
  ).map((edge) => edge.result.value)

  const handleSearchOnClick = async () => {
    // Grab the top result from autocomplete.

    const topLocAutoCompleteResult = options?.[0] ?? null
    const topLocDescription = topLocAutoCompleteResult?.description ?? null
    let topLocResultLat, topLocResultLng, topLocResultLocString

    // if autocomplete results and description doesn't match current location it
    // they haven't selected an autocomplete result. therefore we assume they wanted
    // to use the top result and we will encode it to get lat/long.
    if (topLocAutoCompleteResult && topLocDescription !== location) {
      const {
        placeId,
        latitude,
        longitude,
        addressLine1,
        zipCode,
        city,
        state,
        locationDescription,
      } = await geoCodeOption(topLocAutoCompleteResult)
      setPlaceId(placeId)
      topLocResultLat = latitude
      topLocResultLng = longitude
      topLocResultLocString = locationDescription
    }

    setLocation(topLocResultLocString || location || "")
    setLatitude(topLocResultLat || latitude)
    setLongitude(topLocResultLng || longitude)
    setSearchBarFocused(false)
    handleUpdateSearch({
      newSearchTerm: searchTerm || "",
      newLocation: topLocResultLocString || location || "",
      newLatitude: topLocResultLat || latitude,
      newLongitude: topLocResultLng || longitude,
    })
  }

  const handleClickAway = () => {
    setSearchBarFocused(false)
  }

  const renderSearchBarFocused = () => {
    return (
      <SearchBarFocused
        searchLabel={searchLabel}
        searchPlaceholder={searchPlaceholder}
        searchHeading={searchHeading}
        handleClickAway={handleClickAway}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        location={location}
        setLocation={setLocation}
        setSearchBarFocused={setSearchBarFocused}
        placeId={placeId}
        setPlaceId={setPlaceId}
        latitude={latitude}
        setLatitude={setLatitude}
        longitude={longitude}
        setLongitude={setLongitude}
        handleUpdateSearch={handleSearchOnClick}
        autocompleteGroupOptions={autocompleteGroupOptions}
        value={value}
        inputValue={inputValue}
        options={options}
        setValue={setValue}
        setInputValue={setInputValue}
        setOptions={setOptions}
        geoCodeOption={geoCodeOption}
        fetchPlacePredictions={fetchPlacePredictions}
      />
    )
  }

  const renderSearchBarUnfocused = () => {
    return (
      <div className="" key="unfocused searchbars">
        <SearchBarUnfocused
          searchPlaceholder={searchPlaceholder}
          searchTerm={searchTerm}
          location={location}
          setSearchBarFocused={setSearchBarFocused}
        />
      </div>
    )
  }
  return searchBarFocused ? renderSearchBarFocused() : renderSearchBarUnfocused()
}

export default SearchBar
